import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import {
  setLayout3dLoading,
  setLayout3dReady,
  setLayout3dError,
  clearLayout3d,
} from '../features/layout3d/layout3dSlice';
import { computeBoundsKey } from '../utils/layout3dGenerate';
import { loadLayout3DFromCache, saveLayout3DToCache } from '../utils/layout3dCache';
import { LAYOUT_3D_OBJECT_COUNT } from '../utils/layout3dConstants';
import { scene3dWorkerClient } from '../workers/scene3dWorkerClient';

/**
 * When entering 3D with an imported floor and no 2D elements,
 * generates ~10k colored objects via Web Worker and caches as JSON.
 */
export function use3dLayoutBootstrap(enabled: boolean): {
  loading: boolean;
  ready: boolean;
  objectCount: number;
} {
  const dispatch = useAppDispatch();
  const bounds = useAppSelector((s) => s.floorplan.bounds);
  const scaleFactor = useAppSelector((s) => s.floorplan.scaleFactor);
  const floorName = useAppSelector((s) => s.floorplan.name);
  const layout2dCount = useAppSelector((s) => s.layout.elementOrder.length);
  const layout3d = useAppSelector((s) => s.layout3d);

  const bootstrapRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    if (!bounds || !floorName) {
      dispatch(clearLayout3d());
      return;
    }

    if (layout2dCount > 0) {
      dispatch(clearLayout3d());
      return;
    }

    const boundsKey = computeBoundsKey(bounds, scaleFactor > 0 ? scaleFactor : 20);

    if (layout3d.status === 'ready' && layout3d.boundsKey === boundsKey) {
      return;
    }

    if (bootstrapRef.current === boundsKey && layout3d.status === 'loading') {
      return;
    }

    bootstrapRef.current = boundsKey;
    let cancelled = false;

    const run = async () => {
      dispatch(setLayout3dLoading({ boundsKey }));

      try {
        const cached = loadLayout3DFromCache(boundsKey);
        const sf = scaleFactor > 0 ? scaleFactor : 20;

        const { project, batches } = await scene3dWorkerClient.generateLayout3DWithBatches({
          bounds,
          scaleFactor: sf,
          objectCount: LAYOUT_3D_OBJECT_COUNT,
          boundsKey,
          cachedProject: cached ?? undefined,
        });

        if (cancelled) return;

        if (!cached) {
          saveLayout3DToCache(project);
        }

        dispatch(
          setLayout3dReady({
            boundsKey,
            elements: project.elements,
            elementOrder: project.elementOrder,
            instanceBatches: batches,
            autoGenerated: true,
          })
        );
      } catch (err) {
        if (!cancelled) {
          dispatch(
            setLayout3dError(err instanceof Error ? err.message : 'Failed to build 3D layout')
          );
        }
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    bounds,
    scaleFactor,
    floorName,
    layout2dCount,
    layout3d.status,
    layout3d.boundsKey,
    dispatch,
  ]);

  return {
    loading: layout3d.status === 'loading',
    ready: layout3d.status === 'ready',
    objectCount: layout3d.objectCount,
  };
}
